import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getVideosByCategory, type VideoItem } from '@/mocks/videos';
import { getArticlesByCategory, type ArticleItem } from '@/mocks/articles';

function CategoryVideoCard({ video }: { video: VideoItem }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          title={`${video.title} - 久久视频${video.category}分类`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-medium text-foreground-50 bg-black/70 rounded-md">
          {video.duration}
        </span>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90">
            <i className="ri-play-fill text-2xl text-background-50"></i>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {video.title}
        </h3>
        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500"></i>
            {video.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}

function CategoryArticleCard({ article }: { article: ArticleItem }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <span className="inline-block px-2 py-0.5 text-xs font-medium text-primary-600 bg-primary-100 rounded-full mb-2">
          {article.tags[0]}
        </span>
        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span>{article.readTime}</span>
          <span>·</span>
          <span>{article.views} 阅读</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'videos' | 'articles'>('videos');

  useEffect(() => {
    setActiveTab('videos');
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryVideos = slug ? getVideosByCategory(slug) : [];
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  const categoryName = currentCategory?.name || slug || '';
  const categoryDesc = currentCategory?.description || '';

  const seoJsonLd = slug
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${categoryName} - 久久视频分类`,
        description: categoryDesc,
        url: `https://www.jiujiu-video.com/category/${slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '久久视频',
          url: 'https://www.jiujiu-video.com/',
        },
      }
    : null;

  const breadcrumbJsonLd = slug
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiujiu-video.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName },
        ],
      }
    : null;

  return (
    <>
      <ScrollToTop />
      {seoJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(seoJsonLd) }}
        />
      )}
      {breadcrumbJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      )}

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Category Header */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {/* Breadcrumb */}
              <nav aria-label="面包屑导航" className="mb-6">
                <ol className="flex items-center gap-2 text-xs text-foreground-500">
                  <li>
                    <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                      首页
                    </Link>
                  </li>
                  <li>
                    <i className="ri-arrow-right-s-line"></i>
                  </li>
                  <li className="text-foreground-950 font-medium">{categoryName}</li>
                </ol>
              </nav>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {categoryName}
                  </h1>
                  <p className="mt-2 text-sm text-foreground-600">{categoryDesc}</p>
                </div>
                <div className="text-sm text-foreground-500">
                  共 <strong className="text-foreground-950">{categoryVideos.length + categoryArticles.length}</strong> 个内容
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs Navigation */}
          <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 py-3 overflow-x-auto flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Content Tabs */}
          <section className="py-8 md:py-12 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('videos')}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === 'videos'
                      ? 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-950 bg-background-100'
                  }`}
                >
                  <i className="ri-vidicon-line mr-1.5"></i>
                  视频 ({categoryVideos.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('articles')}
                  className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === 'articles'
                      ? 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-950 bg-background-100'
                  }`}
                >
                  <i className="ri-article-line mr-1.5"></i>
                  文章 ({categoryArticles.length})
                </button>
              </div>

              {activeTab === 'videos' && (
                <>
                  {categoryVideos.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {categoryVideos.map((video) => (
                        <CategoryVideoCard key={video.id} video={video} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                        <i className="ri-vidicon-line text-2xl text-foreground-400"></i>
                      </div>
                      <p className="mt-4 text-sm text-foreground-500">暂无相关内容</p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'articles' && (
                <>
                  {categoryArticles.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {categoryArticles.map((article) => (
                        <CategoryArticleCard key={article.id} article={article} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                        <i className="ri-article-line text-2xl text-foreground-400"></i>
                      </div>
                      <p className="mt-4 text-sm text-foreground-500">暂无相关文章</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}